import styled from '@emotion/styled';
import React from 'react';
import { ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { semantic_colors } from '../../foundation';
import { IconPin, IconPinFill, IconThreeBars } from '../icons/generated';
import { Stack } from '../stack';
import { Switch } from '../switch';
import { BaseText } from '../text';
import { getStylesByPinned, item_base_css, pin_base_css, three_bars_base_css, getDraggableStyle } from './styles';
import { css } from '@emotion/react';

export interface ListItemProps<T = string> {
  id: T;
  text: ReactNode;
  disabled?: boolean;
  /**
   * 항목을 드래그해서 순서를 바꿀 수 있는 기능 확장 여부
   * @default false
   */
  draggable?: boolean;
  /**
   * 드래그 오버레이된 항목 여부
   * @see draggable
   * @default false
   */
  draggingOverlay?: boolean;
  /**
   * draggable=true이면, index는 필수값이 된다
   * @see draggable
   */
  index?: number;
  /**
   * isOn 정의시 토글버튼을 사용한다 (Togglable)
   */
  isOn?: boolean;
  /**
   * isOn 정의시 onToggle은 필수값이 된다
   * @see isOn
   */
  onToggle?: (isOn: boolean, index?: number) => void;
  /**
   * fixed 정의시 핀버튼을 사용한다 (Pinnable)
   */
  fixed?: boolean;
  /**
   * fixed 정의시 onPin은 필수값이 된다
   * @see fixed
   */
  onPin?: (fixed: boolean, index?: number) => void;
}

export const ListItem = <T extends string>({
  id,
  isOn,
  fixed,
  disabled,
  text,
  draggable = false,
  draggingOverlay = false,
  index,
  onToggle,
  onPin,
}: ListItemProps<T>) => {
  if (draggable && index == null) {
    throw new Error("Draggable ListItem은 'index' 속성이 존재해야합니다.");
  }
  if (typeof isOn === 'boolean' && !onToggle) {
    throw new Error("Togglable ListItem은 'onToggle' 속성이 존재해야합니다.");
  }
  if (typeof fixed === 'boolean' && !onPin) {
    throw new Error("Pinnable ListItem은 'onPin' 속성이 존재해야합니다.");
  }

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !draggable || disabled,
  });

  const renderItem = () => (
    <Stack
      direction='row'
      width='100%'
      align='stretch'
      justify='space-between'
      p={typeof fixed === 'boolean' || draggable ? '9px 8px 9px 12px' : '9px 12px 9px'}
      css={item_base_css}
      data-testid='list-item'
      aria-disabled={disabled}
    >
      <Stack direction='row' gap={8} align='center' height={24}>
        {typeof isOn === 'boolean' && (
          <StyledSwitch
            isOn={isOn}
            onText=''
            offText=''
            onClick={() => onToggle?.(!isOn, index)}
            disabled={disabled}
            data-testid='list-item-switch'
          />
        )}
        <BaseText
          kind='Body_13_Regular'
          color={disabled ? semantic_colors.content.disabled : semantic_colors.content.primary}
          data-testid='list-item-text'
        >
          {text}
        </BaseText>
      </Stack>
      {!disabled && (typeof fixed === 'boolean' || draggable) && (
        <Stack direction='row' align='center'>
          {typeof fixed === 'boolean' &&
            (fixed ? (
              <IconPinFill
                css={[pin_base_css, getStylesByPinned(fixed)]}
                onClick={() => onPin?.(!fixed, index)}
                data-testid='list-item-pin-fixed'
              />
            ) : (
              <IconPin
                css={[pin_base_css, getStylesByPinned(fixed)]}
                onClick={() => onPin?.(!fixed, index)}
                data-testid='list-item-pin'
              />
            ))}
          {draggable && <IconThreeBars css={three_bars_base_css} data-testid='list-item-drag-handle' />}
        </Stack>
      )}
    </Stack>
  );

  return draggable ? (
    <div
      ref={setNodeRef}
      css={[
        getDraggableStyle(isDragging, draggable && draggingOverlay, disabled),
        css`
          transform: ${transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined};
          transition: ${transition};
        `,
      ]}
      {...attributes}
      {...listeners}
      data-testid='list-item-draggable-wrapper'
    >
      {renderItem()}
    </div>
  ) : (
    renderItem()
  );
};

const StyledSwitch = styled(Switch)`
  width: 36px;
`;
